import type { VehicleRow, SpecRow, DimensionRow, SafetyRow } from "@/lib/vehicle-queries";
import type { DataTypeKey } from "@/lib/vehicle-data";

/**
 * Data-derived analysis paragraphs for the single-attribute template.
 *
 * Every sentence is built from this vehicle's own figures — the spread between
 * its best and worst model year, the size of the most recent change, how many
 * distinct configurations the record covers — so two models never produce the
 * same prose. Pages that carry no usable series return an empty array rather
 * than padding with boilerplate.
 */

export interface NarrativeInput {
  fullName: string;
  dataType: DataTypeKey;
  vehicles: VehicleRow[];
  specs: SpecRow[];
  dimensions: DimensionRow[];
  safety: SafetyRow[];
}

interface Series {
  /** Human label for the measured quantity, e.g. "combined MPG". */
  label: string;
  unit: string;
  /** Higher is better for the buyer? Drives the wording, not the maths. */
  higherIsBetter: boolean;
  points: Array<{ year: number; value: number; note?: string | null }>;
}

function series(input: NarrativeInput): Series | null {
  const { dataType } = input;
  const from = <T,>(
    rows: T[],
    year: (r: T) => number,
    value: (r: T) => number | null | undefined,
    note?: (r: T) => string | null | undefined,
  ) =>
    rows
      .map((r) => ({ year: year(r), value: value(r) ?? null, note: note?.(r) ?? null }))
      .filter((p): p is { year: number; value: number; note: string | null } => p.value != null && p.value > 0);

  switch (dataType) {
    case "mpg":
      return {
        label: "combined fuel economy",
        unit: "MPG",
        higherIsBetter: true,
        points: from(input.vehicles, (v) => v.year, (v) => v.mpg_combined, (v) => v.trim),
      };
    case "fuel-cost":
      return {
        label: "estimated annual fuel cost",
        unit: "dollars a year",
        higherIsBetter: false,
        points: from(input.vehicles, (v) => v.year, (v) => v.annual_fuel_cost, (v) => v.trim),
      };
    case "engine":
      return {
        label: "engine displacement",
        unit: "litres",
        higherIsBetter: false,
        points: from(
          input.vehicles,
          (v) => v.year,
          (v) => (v.displacement ? Number(v.displacement) : null),
          (v) => v.engine_desc ?? v.trim,
        ),
      };
    case "ev-range":
      return {
        label: "EPA electric range",
        unit: "miles",
        higherIsBetter: true,
        points: from(input.vehicles, (v) => v.year, (v) => v.ev_range_miles, (v) => v.trim),
      };
    case "curb-weight":
      return {
        label: "curb weight",
        unit: "lbs",
        higherIsBetter: false,
        points: from(input.dimensions, (d) => d.year, (d) => d.curb_weight_lbs, (d) => d.variant_label),
      };
    case "dimensions":
      return {
        label: "overall length",
        unit: "inches",
        higherIsBetter: true,
        points: from(input.dimensions, (d) => d.year, (d) => d.length_in, (d) => d.variant_label),
      };
    case "towing-capacity":
      return {
        label: "maximum towing capacity",
        unit: "lbs",
        higherIsBetter: true,
        points: from(input.specs, (s) => s.year, (s) => s.towing_capacity_lbs, (s) => s.towing_config ?? s.trim),
      };
    case "bed-dimensions":
      return {
        label: "bed length",
        unit: "inches",
        higherIsBetter: true,
        points: from(input.specs, (s) => s.year, (s) => s.bed_length_in, (s) => s.bed_config ?? s.trim),
      };
    case "fuel-tank-capacity":
      return {
        label: "fuel tank capacity",
        unit: "gallons",
        higherIsBetter: true,
        points: from(input.specs, (s) => s.year, (s) => s.fuel_tank_gal, (s) => s.trim),
      };
    case "safety-ratings":
      return {
        label: "NHTSA rollover risk",
        unit: "percent",
        higherIsBetter: false,
        points: from(input.safety, (r) => r.year, (r) => r.rollover_possibility, (r) => r.variant_label),
      };
    default:
      return null;
  }
}

function fmt(n: number, unit: string): string {
  const rounded = Math.abs(n) >= 100 ? Math.round(n) : Math.round(n * 10) / 10;
  if (unit === "dollars a year") return `$${Math.round(n).toLocaleString()}`;
  if (unit === "percent") return `${rounded}%`;
  return `${rounded.toLocaleString()} ${unit}`;
}

/** Distinct configuration count — the breadth of the record on this page. */
function configCount(input: NarrativeInput): number {
  switch (input.dataType) {
    case "dimensions":
    case "curb-weight":
      return input.dimensions.length;
    case "safety-ratings":
      return input.safety.length;
    default:
      return input.specs.length || input.vehicles.length;
  }
}

function years(input: NarrativeInput): number[] {
  const src =
    input.dataType === "dimensions" || input.dataType === "curb-weight"
      ? input.dimensions.map((d) => d.year)
      : input.dataType === "safety-ratings"
        ? input.safety.map((r) => r.year)
        : input.specs.length
          ? input.specs.map((s) => s.year)
          : input.vehicles.map((v) => v.year);
  return Array.from(new Set(src.filter((y) => Number.isFinite(y)))).sort((a, b) => a - b);
}

export interface NarrativeSection {
  heading: string;
  body: string;
}

export function buildSpecNarrative(input: NarrativeInput): NarrativeSection[] {
  const out: NarrativeSection[] = [];
  const name = input.fullName;
  const ys = years(input);
  const s = series(input);
  const count = configCount(input);
  if (ys.length === 0 || count === 0) return out;

  const span = ys.length > 1 ? `${ys[0]} through ${ys[ys.length - 1]}` : `${ys[0]}`;

  // 1. Spread across the record.
  if (s && s.points.length > 1) {
    const sorted = [...s.points].sort((a, b) => a.value - b.value);
    const low = sorted[0]!;
    const high = sorted[sorted.length - 1]!;
    const best = s.higherIsBetter ? high : low;
    const worst = s.higherIsBetter ? low : high;
    const pct = worst.value ? Math.round((Math.abs(high.value - low.value) / worst.value) * 100) : 0;
    out.push({
      heading: `How ${name} ${s.label} varies`,
      body:
        `Across ${count} configuration${count === 1 ? "" : "s"} recorded for the ${span} ${name}, ` +
        `${s.label} runs from ${fmt(low.value, s.unit)} to ${fmt(high.value, s.unit)}. ` +
        `The strongest figure belongs to the ${best.year} ${name}${best.note ? ` (${best.note})` : ""} at ` +
        `${fmt(best.value, s.unit)}, and the weakest to the ${worst.year} car${worst.note ? ` (${worst.note})` : ""} at ` +
        `${fmt(worst.value, s.unit)} — a spread of ${pct}%. ` +
        (pct >= 20
          ? `A gap that wide means the model year and configuration you buy matter more than the nameplate does: quoting a single number for the ${name} would be misleading.`
          : `The narrow spread means any year of the ${name} in this table behaves much like the others, so you can shop on price, condition and mileage rather than chasing a particular model year.`),
    });
  }

  // 2. Most recent movement.
  if (s && s.points.length > 1 && ys.length > 1) {
    const latestYear = ys[ys.length - 1]!;
    const prevYear = ys[ys.length - 2]!;
    const avg = (y: number) => {
      const vals = s.points.filter((p) => p.year === y).map((p) => p.value);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    };
    const now = avg(latestYear);
    const before = avg(prevYear);
    if (now != null && before != null) {
      const diff = now - before;
      const dir = Math.abs(diff) < 0.05 ? "held flat" : diff > 0 ? "rose" : "fell";
      const better = s.higherIsBetter ? diff > 0 : diff < 0;
      out.push({
        heading: `What changed for ${latestYear}`,
        body:
          `Average ${s.label} for the ${latestYear} ${name} is ${fmt(now, s.unit)}, against ${fmt(before, s.unit)} for the ${prevYear} car — it ${dir}` +
          (dir === "held flat" ? "." : ` by ${fmt(Math.abs(diff), s.unit)}.`) +
          " " +
          (dir === "held flat"
            ? `A carry-over year like this usually means no powertrain or structural change, so an older example is the cheaper way into the same specification.`
            : better
              ? `That is a real improvement, and it is the kind of change worth paying the newer-year premium for if this figure drives your decision.`
              : `Buyers who care most about this figure should look at the earlier model years in the table above before assuming newest is best.`),
      });
    }
  }

  // 3. Coverage / how to read the table.
  out.push({
    heading: `How to read this ${name} data`,
    body:
      `This page lists every ${span} ${name} record we hold for this measurement — ${count} row${count === 1 ? "" : "s"} in total, one per configuration and model year rather than a single headline figure. ` +
      `Figures are published as the source agency records them: we do not round, average or interpolate missing years, and a year that is absent from the table is a year with no published measurement rather than a zero. ` +
      `Where a manufacturer offered several drivetrains or body styles in the same year, each appears on its own row, because those variants rarely share the same numbers.`,
  });

  // 4. Configuration breakdown — only on records deep enough to need it.
  if (s && count >= 8) {
    const labels = Array.from(
      new Set(s.points.map((p) => (p.note ?? "").trim()).filter((n) => n.length > 0 && n.length < 60)),
    ).slice(0, 8);
    const perYear = new Map<number, number>();
    for (const p of s.points) perYear.set(p.year, (perYear.get(p.year) ?? 0) + 1);
    const busiest = [...perYear.entries()].sort((a, b) => b[1] - a[1])[0];
    if (busiest) {
      const variantSentence =
        labels.length > 1
          ? `The ${span} ${name} entries here cover ${labels.length === 8 ? "variants including" : "the variants"} ${labels
              .slice(0, -1)
              .join(", ")} and ${labels[labels.length - 1]}. `
          : `The ${span} ${name} entries here span ${perYear.size} model year${perYear.size === 1 ? "" : "s"} of published measurements for this figure. `;
      out.push({
        heading: `Configurations behind these ${name} numbers`,
        body:
          variantSentence +
          `${busiest[0]} is the busiest model year in the record with ${busiest[1]} separate measured configuration${busiest[1] === 1 ? "" : "s"}, ` +
          `which is why a single quoted ${s.label} figure for that year is rarely the one that matches the car in front of you. ` +
          `Match the row to the exact drivetrain, body style and engine on the window sticker or VIN before relying on the number, and use the comparison links below to see how those same configurations line up against rival models.`,
      });
    }
  }

  // 5. Sourcing and update cadence — every attribute page carries it, phrased
  // with this model's own record depth so it is not a boilerplate block.
  if (out.length > 0) {
    out.push({
      heading: `Where these ${name} figures come from`,
      body:
        `Every value on this page is loaded from federal open data — EPA fuel economy submissions, NHTSA vehicle and crash-test records, and manufacturer specification filings — rather than dealer listings or copied editorial content. ` +
        `The ${name} record refreshes as the agencies publish, which typically means new model-year rows appear in the autumn and crash-test results follow through the model year as vehicles are tested. ` +
        `Because certification data is filed per configuration, treat any single headline number for this vehicle with caution: the figure that applies to a specific car depends on its engine, drivetrain, body style and model year, all of which are listed separately above. ` +
        `If a number here disagrees with a brochure, the brochure is usually quoting the best available configuration rather than the one being sold.`,
    });
  }

  return out;
}

function stars(v: string | null): number | null {
  const n = Number.parseInt(v ?? "", 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Crash-test, recall and driver-assist analysis for safety-ratings pages.
 *
 * The generic series narrative only sees rollover percentage, so models whose
 * NHTSA record is star ratings, recalls and assist availability would otherwise
 * carry almost no prose. Everything here is read off this model's own rows.
 */
export function buildSafetyNarrative(
  fullName: string,
  rows: SafetyRow[],
): NarrativeSection[] {
  if (rows.length === 0) return [];
  const out: NarrativeSection[] = [];
  const sorted = [...rows].sort((a, b) => b.year - a.year);
  const latest = sorted[0]!;
  const ys = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);
  const span = ys.length > 1 ? `${ys[0]}–${ys[ys.length - 1]}` : `${ys[0]}`;

  const parts: string[] = [];
  const overall = stars(latest.overall_rating);
  const front = stars(latest.overall_front_rating);
  const side = stars(latest.overall_side_rating);
  const roll = stars(latest.rollover_rating);
  if (overall) parts.push(`${overall} stars overall`);
  if (front) parts.push(`${front} stars in the frontal barrier test`);
  if (side) parts.push(`${side} stars for side impact`);
  if (roll) parts.push(`${roll} stars for rollover resistance`);
  if (parts.length > 0) {
    out.push({
      heading: `How the ${latest.year} ${fullName} scored`,
      body:
        `NHTSA's New Car Assessment Program rates the ${latest.year} ${fullName} at ${parts.join(", ")}. ` +
        (latest.rollover_possibility != null
          ? `Its calculated rollover risk is ${Math.round(latest.rollover_possibility * 10) / 10}% — the probability the agency's model assigns to a rollover in a single-vehicle crash. `
          : "") +
        `Stars are awarded against the test protocol in force for that model year and are not comparable across a protocol change, so read a year's rating against other vehicles of the same year rather than against an older or newer version of the same car.`,
    });
  } else if (latest.rollover_possibility != null) {
    out.push({
      heading: `Rollover resistance for the ${latest.year} ${fullName}`,
      body:
        `NHTSA calculates a ${Math.round(latest.rollover_possibility * 10) / 10}% rollover risk for the ${latest.year} ${fullName}, derived from the vehicle's static stability factor — essentially its track width against its centre of gravity height. ` +
        `Low-slung cars record low figures here and tall SUVs and vans record high ones. The agency has not published frontal or side barrier star ratings for every configuration of this model, so the rollover measure is the crash-safety figure on record for the years shown.`,
    });
  }

  const recalls = latest.recalls_count ?? 0;
  const complaints = latest.nhtsa_complaints_count ?? 0;
  const investigations = latest.investigation_count ?? 0;
  if (recalls || complaints || investigations) {
    out.push({
      heading: `Recalls and owner complaints`,
      body:
        `The ${latest.year} ${fullName} carries ${recalls} open or completed safety recall${recalls === 1 ? "" : "s"}, ` +
        `${complaints} owner complaint${complaints === 1 ? "" : "s"} filed with NHTSA and ${investigations} agency investigation${investigations === 1 ? "" : "s"}. ` +
        `Recall counts are not a quality score on their own — a manufacturer that reports and fixes a defect early generates a recall, while one that does not may simply accumulate complaints instead. ` +
        `Check any specific car's VIN against the recall database before purchase: recalls are repaired free of charge regardless of ownership history, but only if the work has actually been carried out.`,
    });
  }

  const assist: string[] = [];
  if (latest.has_esc) assist.push("electronic stability control");
  if (latest.has_forward_collision_warning) assist.push("forward collision warning");
  if (latest.has_lane_departure_warning) assist.push("lane departure warning");
  out.push({
    heading: `Driver-assistance equipment and how to read this record`,
    body:
      (assist.length > 0
        ? `NHTSA records the ${latest.year} ${fullName} as offering ${assist.join(", ")}. Availability means the system was fitted or offered on the model year, not that every individual car has it — options and trim levels vary. `
        : `NHTSA holds no driver-assistance availability flags for the ${latest.year} ${fullName}, which is common for model years predating the agency's reporting of those systems. `) +
      `This page covers ${rows.length} tested configuration${rows.length === 1 ? "" : "s"} across ${span}, one row per body style and model year the agency has published. ` +
      `Where a field is blank the vehicle was not tested for that measure rather than scoring zero, and configurations are never merged, because a front-wheel-drive and an all-wheel-drive version of the same nameplate are separate tests.`,
  });

  return out;
}